import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { CameraCapture } from "@/components/CameraCapture";
import { QuestionBuilder, validateQuestions } from "@/components/QuestionBuilder";
import { toast } from "sonner";
import { Upload, Sparkles, X, ArrowLeft, CheckCircle2, RefreshCw, FileText, Table2, Link2, Plus, Image as ImageIcon, Camera, SquarePen, Wand2, FileUp, Library } from "lucide-react";

const TYPES = ["Reading", "Vocabulary", "Questions", "Essay", "Quiz"];
const IMAGE_TYPES = ["png", "jpg", "jpeg", "jfif", "webp", "gif", "bmp", "heic", "heif", "tif", "tiff"];
const PREVIEWABLE = ["png", "jpg", "jpeg", "jfif", "webp", "gif", "bmp"];
const DOC_ICON = { pdf: FileText, doc: FileText, docx: FileText, xls: Table2, xlsx: Table2 };
const fileExt = (name) => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");

async function fileToB64(file) {
  return new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1]);
    r.readAsDataURL(file);
  });
}

const MODES = [
  { key: "ai", label: "AI generate", icon: Wand2, desc: "Upload materials and let the AI build the lesson." },
  { key: "manual", label: "Build your own", icon: SquarePen, desc: "Write a reading and add your own questions." },
  { key: "upload", label: "Upload a file", icon: FileUp, desc: "Attach a PDF / Word / photo — nothing generated." },
];

export default function CreateAssignment() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("ai");
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({
    student_id: "", subject: "", book: "", chapter: "", reading_level: "7",
    grade_level: "", assignment_types: ["Reading", "Vocabulary", "Questions"], teacher_instructions: "",
    title: "", due_date: "",
  });
  const [files, setFiles] = useState([]);
  const [links, setLinks] = useState([]);
  const [linkInput, setLinkInput] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lesson, setLesson] = useState(null);
  const [images, setImages] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [savingLib, setSavingLib] = useState(false);
  const [reading, setReading] = useState("");
  const [questions, setQuestions] = useState([]);

  useEffect(() => { api.get("/students").then((r) => setStudents(r.data)); }, []);

  const pickStudent = (id) => {
    const s = students.find((x) => x.id === id);
    setForm((f) => ({ ...f, student_id: id, grade_level: String(s?.grade_level ?? ""), reading_level: String(s?.reading_level ?? "7") }));
    api.get(`/subjects?student_id=${id}`).then((r) => setSubjects(r.data));
  };

  const onFiles = async (fl) => {
    const arr = Array.from(fl);
    const withB64 = await Promise.all(arr.map(async (f) => {
      const ext = fileExt(f.name);
      const isImage = IMAGE_TYPES.includes(ext);
      const previewable = PREVIEWABLE.includes(ext);
      return { name: f.name, ext, isImage, previewable, url: previewable ? URL.createObjectURL(f) : null, b64: await fileToB64(f) };
    }));
    setFiles((prev) => [...prev, ...withB64]);
  };

  const addLink = () => {
    const v = linkInput.trim();
    if (!v) return;
    if (!/docs\.google\.com\/(document|spreadsheets)\//.test(v)) {
      return toast.error("Paste a Google Docs or Google Sheets share link.");
    }
    setLinks((l) => [...l, v]);
    setLinkInput("");
  };

  const toggleType = (t) =>
    setForm((f) => ({ ...f, assignment_types: f.assignment_types.includes(t) ? f.assignment_types.filter((x) => x !== t) : [...f.assignment_types, t] }));

  const generate = async () => {
    if (!form.student_id || !form.subject || !form.chapter) return toast.error("Choose a student, subject and enter a chapter.");
    if (!files.length && !links.length) return toast.error("Upload at least one file or add a Google Docs/Sheets link.");
    setGenerating(true);
    try {
      const { data } = await api.post("/assignments/generate-lesson", {
        ...form,
        files: files.map((f) => ({ name: f.name, b64: f.b64 })),
        links,
      });
      setLesson(data.lesson);
      setImages(data.images || []);
      if (data.warnings?.length) data.warnings.forEach((w) => toast.warning(w));
      toast.success("Lesson generated — review before publishing.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };

  const publish = async (published) => {
    setPublishing(true);
    try {
      await api.post("/assignments", {
        student_id: form.student_id, subject: form.subject, chapter: form.chapter,
        title: lesson.chapter_title || `${form.subject} ${form.chapter}`,
        reading_level: form.reading_level, lesson, teacher_instructions: form.teacher_instructions, published,
        original_images: images, due_date: form.due_date || null,
      });
      toast.success(published ? "Assignment published!" : "Draft saved.");
      navigate("/parent");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally { setPublishing(false); }
  };

  const buildCustomLesson = async () => {
    let attachments = [];
    if (mode === "upload" && files.length) {
      const { data } = await api.post("/assignments/upload-attachments", {
        files: files.map((f) => ({ name: f.name, b64: f.b64 })),
      });
      attachments = data.attachments || [];
    }
    return {
      chapter_title: form.title,
      summary: reading,
      attachments,
      questions: questions.map((q) => ({ ...q, points: Number(q.points) || 0 })),
      estimated_minutes: 20,
    };
  };

  const validateCustom = () => {
    if (!form.subject.trim() || !form.title.trim()) { toast.error("Enter a subject and a title."); return false; }
    const qErr = validateQuestions(questions);
    if (qErr) { toast.error(qErr); return false; }
    if (mode === "manual" && !reading.trim() && questions.length === 0) { toast.error("Add a reading or at least one question."); return false; }
    if (mode === "upload" && !files.length && !reading.trim()) { toast.error("Attach at least one file (or type a reading)."); return false; }
    return true;
  };

  const publishCustom = async (published) => {
    if (!form.student_id) return toast.error("Choose a student (or use Save to Library to assign later).");
    if (!validateCustom()) return;
    setPublishing(true);
    try {
      const lessonDoc = await buildCustomLesson();
      await api.post("/assignments", {
        student_id: form.student_id, subject: form.subject.trim(),
        chapter: form.chapter || form.title, title: form.title,
        reading_level: form.reading_level, lesson: lessonDoc,
        teacher_instructions: form.teacher_instructions, published,
        original_images: [], due_date: form.due_date || null,
      });
      toast.success(published ? "Assignment published!" : "Draft saved.");
      navigate("/parent");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally { setPublishing(false); }
  };

  const saveToLibrary = async (lessonDoc, title, subject, source) => {
    setSavingLib(true);
    try {
      await api.post("/library", {
        title, subject: (subject || "").trim(), chapter: form.chapter || title,
        reading_level: form.reading_level, lesson: lessonDoc,
        teacher_instructions: form.teacher_instructions, source,
      });
      toast.success("Saved to your Lesson Library!");
      navigate("/parent/library");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally { setSavingLib(false); }
  };

  const saveCustomToLibrary = async () => {
    if (!validateCustom()) return;
    const lessonDoc = await buildCustomLesson().catch((e) => { toast.error(e.message); return null; });
    if (lessonDoc) await saveToLibrary(lessonDoc, form.title, form.subject, mode === "upload" ? "upload" : "manual");
  };

  const FileChips = () => (
    files.length > 0 && (
      <div className="flex flex-wrap gap-3 mt-4">
        {files.map((f, i) => {
          const Icon = f.isImage ? ImageIcon : (DOC_ICON[f.ext] || FileText);
          return f.previewable ? (
            <div key={i} data-testid={`file-chip-${i}`} className="relative w-24 h-28 rounded-xl overflow-hidden border border-[#EAE6DF]">
              <img src={f.url} alt={f.name} className="w-full h-full object-cover" />
              <button onClick={() => setFiles(files.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5"><X size={13} /></button>
            </div>
          ) : (
            <div key={i} data-testid={`file-chip-${i}`} className="relative flex items-center gap-2.5 rounded-xl border border-[#EAE6DF] bg-[#F7F5F0] px-3 py-2.5 pr-8 max-w-[220px]">
              <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#81B29A]/15 text-[#5f8c76] shrink-0"><Icon size={18} /></span>
              <span className="text-xs text-[#3D405B] truncate">{f.name}</span>
              <button onClick={() => setFiles(files.filter((_, x) => x !== i))} className="absolute top-1.5 right-1.5 text-[#6B7280] hover:text-[#E07A5F]"><X size={14} /></button>
            </div>
          );
        })}
      </div>
    )
  );

  return (
    <div>
      <TopBar subtitle="Create assignment" />
      <CameraCapture open={cameraOpen} onClose={() => setCameraOpen(false)}
        onDone={(captured) => { setFiles((prev) => [...prev, ...captured]); toast.success(`Added ${captured.length} page${captured.length === 1 ? "" : "s"} from the camera.`); }} />
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => navigate("/parent")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6"><ArrowLeft size={16} /> Back</button>
        <h1 className="font-head text-4xl font-bold text-[#3D405B] mb-6">Create an assignment</h1>

        {!lesson && (
          <>
            <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="mode-selector">
              {MODES.map((m) => (
                <button key={m.key} data-testid={`mode-${m.key}`} onClick={() => setMode(m.key)}
                  className={`text-left rounded-2xl border p-4 transition-colors ${mode === m.key ? "border-[#E07A5F] bg-[#E07A5F]/5 ring-1 ring-[#E07A5F]" : "border-[#EAE6DF] bg-white hover:border-[#E07A5F]"}`}>
                  <span className={`inline-grid place-items-center w-9 h-9 rounded-xl mb-2 ${mode === m.key ? "bg-[#E07A5F] text-white" : "bg-[#F2EFE9] text-[#E07A5F]"}`}><m.icon size={18} /></span>
                  <p className="font-semibold text-[#3D405B]">{m.label}</p>
                  <p className="text-xs text-[#6B7280] mt-0.5">{m.desc}</p>
                </button>
              ))}
            </div>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Student">
                  <select data-testid="select-student" value={form.student_id} onChange={(e) => pickStudent(e.target.value)} className="inp">
                    <option value="">Choose…</option>
                    {students.map((s) => <option key={s.id} value={s.id}>{s.first_name} (Grade {s.grade_level})</option>)}
                  </select>
                </Field>
                <Field label="Subject">
                  <input data-testid="input-subject" list="subject-list" value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })} className="inp" placeholder="Pick or type a subject…" />
                  <datalist id="subject-list">{subjects.map((s) => <option key={s.id} value={s.name} />)}</datalist>
                </Field>
                {mode === "ai" ? (
                  <Field label="Chapter(s)">
                    <input data-testid="input-chapter" value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} className="inp" placeholder="e.g. Chapter 7, or Chapters 3–5" />
                  </Field>
                ) : (
                  <Field label="Title">
                    <input data-testid="input-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="inp" placeholder="e.g. Chapter 5 Reading & Quiz" />
                  </Field>
                )}
                <Field label="Due date (optional)">
                  <input type="date" data-testid="input-due-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="inp" />
                </Field>
                <Field label="Reading level (grade)">
                  <input data-testid="input-reading-level" value={form.reading_level} onChange={(e) => setForm({ ...form, reading_level: e.target.value })} className="inp" />
                </Field>
              </div>

              {mode === "ai" && (
                <Field label="Book (optional)">
                  <input data-testid="input-book" value={form.book} onChange={(e) => setForm({ ...form, book: e.target.value })} className="inp" placeholder="e.g. Biology" />
                </Field>
              )}

              <Field label="Teacher instructions (optional)">
                <textarea data-testid="input-instructions" value={form.teacher_instructions} onChange={(e) => setForm({ ...form, teacher_instructions: e.target.value })}
                  className="inp min-h-[70px]" placeholder="Anything the student should keep in mind." />
              </Field>

              {mode === "ai" && (
                <>
                  <div>
                    <label className="text-sm font-medium text-[#3D405B]">Assignment type</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {TYPES.map((t) => (
                        <button key={t} data-testid={`type-${t}`} onClick={() => toggleType(t)}
                          className={`text-sm px-4 py-2 rounded-full border transition-colors ${form.assignment_types.includes(t) ? "bg-[#E07A5F] text-white border-[#E07A5F]" : "bg-white border-[#EAE6DF] hover:border-[#E07A5F]"}`}>{t}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#3D405B]">Lesson materials</label>
                    <p className="text-xs text-[#6B7280] mt-0.5 mb-2">Upload textbook pages or planning files — JPG, PNG, PDF, Word, Excel. The AI reads them all and builds the lesson.</p>
                    <label data-testid="upload-dropzone" className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#EAE6DF] bg-white p-10 cursor-pointer hover:border-[#E07A5F] transition-colors">
                      <Upload size={26} className="text-[#E07A5F]" />
                      <span className="text-sm text-[#6B7280]">Click to add files, or take a photo</span>
                      <input type="file" accept="image/*,.jfif,.heic,.heif,.tif,.tiff,.pdf,.docx,.xlsx" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
                    </label>
                    <button type="button" data-testid="open-camera-btn" onClick={() => setCameraOpen(true)}
                      className="mt-3 flex items-center gap-2 rounded-full border border-[#E07A5F] text-[#E07A5F] px-5 py-2.5 text-sm font-semibold hover:bg-[#E07A5F]/10 transition-colors">
                      <Camera size={16} /> Snap pages with camera
                    </button>
                    <FileChips />
                    <div className="mt-5">
                      <label className="text-sm font-medium text-[#3D405B] flex items-center gap-1.5"><Link2 size={15} className="text-[#457B9D]" /> Google Docs / Sheets link</label>
                      <div className="flex gap-2 mt-2">
                        <input data-testid="gdoc-link-input" value={linkInput} onChange={(e) => setLinkInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }}
                          className="inp flex-1" placeholder="https://docs.google.com/document/d/…" />
                        <button data-testid="add-gdoc-link" type="button" onClick={addLink}
                          className="flex items-center gap-1.5 rounded-xl border border-[#457B9D] text-[#457B9D] px-4 font-semibold text-sm hover:bg-[#457B9D]/10 transition-colors"><Plus size={16} /> Add</button>
                      </div>
                      {links.length > 0 && (
                        <div className="flex flex-col gap-2 mt-3">
                          {links.map((l, i) => (
                            <div key={i} data-testid={`link-chip-${i}`} className="flex items-center gap-2.5 rounded-xl border border-[#EAE6DF] bg-[#F7F5F0] px-3 py-2">
                              <Link2 size={15} className="text-[#457B9D] shrink-0" />
                              <span className="text-xs text-[#3D405B] truncate flex-1">{l}</span>
                              <button onClick={() => setLinks(links.filter((_, x) => x !== i))} className="text-[#6B7280] hover:text-[#E07A5F] shrink-0"><X size={14} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <button data-testid="generate-lesson-btn" onClick={generate} disabled={generating}
                    className="flex items-center gap-2 rounded-full bg-[#E07A5F] text-white px-8 py-3.5 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">
                    <Sparkles size={18} /> {generating ? "Reading your materials & building lesson…" : "Generate Lesson"}
                  </button>
                </>
              )}

              {mode === "upload" && (
                <div>
                  <label className="text-sm font-medium text-[#3D405B]">Attach your lesson file(s)</label>
                  <p className="text-xs text-[#6B7280] mt-0.5 mb-2">PDF, Word (.docx), or photos. Word files are converted to PDF so students see them right on the page. Nothing is AI‑generated.</p>
                  <label data-testid="upload-dropzone" className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#EAE6DF] bg-white p-10 cursor-pointer hover:border-[#E07A5F] transition-colors">
                    <Upload size={26} className="text-[#E07A5F]" />
                    <span className="text-sm text-[#6B7280]">Click to add a PDF, Word doc, or photo</span>
                    <input type="file" accept="image/*,.jfif,.heic,.heif,.pdf,.doc,.docx" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
                  </label>
                  <button type="button" data-testid="open-camera-btn" onClick={() => setCameraOpen(true)}
                    className="mt-3 flex items-center gap-2 rounded-full border border-[#E07A5F] text-[#E07A5F] px-5 py-2.5 text-sm font-semibold hover:bg-[#E07A5F]/10 transition-colors">
                    <Camera size={16} /> Snap pages with camera
                  </button>
                  <FileChips />
                  <div className="mt-6">
                    <label className="text-sm font-medium text-[#3D405B]">Reading / instructions (optional)</label>
                    <textarea data-testid="input-reading" value={reading} onChange={(e) => setReading(e.target.value)}
                      className="inp min-h-[100px] mt-1" placeholder="Type any reading text or directions to show above the file…" />
                  </div>
                  <div className="mt-6">
                    <label className="text-sm font-medium text-[#3D405B] block mb-1">Questions (optional)</label>
                    <p className="text-xs text-[#6B7280] mb-3">Leave empty for a read‑only assignment — the student just taps "Mark complete".</p>
                    <QuestionBuilder questions={questions} setQuestions={setQuestions} />
                  </div>
                  <div className="flex flex-wrap gap-3 mt-8">
                    <button data-testid="save-draft-btn" onClick={() => publishCustom(false)} disabled={publishing} className="rounded-full border border-[#EAE6DF] px-6 py-3 hover:border-[#3D405B] transition-colors">Save draft</button>
                    <button data-testid="save-library-btn" onClick={saveCustomToLibrary} disabled={savingLib} className="flex items-center gap-2 rounded-full border border-[#457B9D] text-[#457B9D] px-6 py-3 font-semibold hover:bg-[#457B9D]/10 transition-colors disabled:opacity-60"><Library size={17} /> {savingLib ? "Saving…" : "Save to Library"}</button>
                    <button data-testid="publish-btn" onClick={() => publishCustom(true)} disabled={publishing}
                      className="flex items-center gap-2 rounded-full bg-[#81B29A] text-white px-8 py-3 font-semibold hover:bg-[#6f9d87] transition-colors disabled:opacity-60"><CheckCircle2 size={18} /> {publishing ? "Publishing…" : "Publish assignment"}</button>
                  </div>
                </div>
              )}

              {mode === "manual" && (
                <div>
                  <label className="text-sm font-medium text-[#3D405B]">Reading (optional)</label>
                  <textarea data-testid="input-reading" value={reading} onChange={(e) => setReading(e.target.value)}
                    className="inp min-h-[140px] mt-1" placeholder="Write the passage or lesson text the student should read…" />
                  <div className="mt-8">
                    <label className="text-sm font-medium text-[#3D405B] block mb-3">Questions</label>
                    <QuestionBuilder questions={questions} setQuestions={setQuestions} />
                  </div>
                  <div className="flex flex-wrap gap-3 mt-8">
                    <button data-testid="save-draft-btn" onClick={() => publishCustom(false)} disabled={publishing} className="rounded-full border border-[#EAE6DF] px-6 py-3 hover:border-[#3D405B] transition-colors">Save draft</button>
                    <button data-testid="save-library-btn" onClick={saveCustomToLibrary} disabled={savingLib} className="flex items-center gap-2 rounded-full border border-[#457B9D] text-[#457B9D] px-6 py-3 font-semibold hover:bg-[#457B9D]/10 transition-colors disabled:opacity-60"><Library size={17} /> {savingLib ? "Saving…" : "Save to Library"}</button>
                    <button data-testid="publish-btn" onClick={() => publishCustom(true)} disabled={publishing}
                      className="flex items-center gap-2 rounded-full bg-[#81B29A] text-white px-8 py-3 font-semibold hover:bg-[#6f9d87] transition-colors disabled:opacity-60"><CheckCircle2 size={18} /> {publishing ? "Publishing…" : "Publish assignment"}</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {lesson && (
          <div data-testid="lesson-review" className="space-y-6">
            <div className="rounded-2xl bg-white border border-[#EAE6DF] p-8">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#81B29A]">AI generated lesson · review before publishing</span>
              <input value={lesson.chapter_title || ""} onChange={(e) => setLesson({ ...lesson, chapter_title: e.target.value })}
                data-testid="edit-title" className="mt-3 w-full font-head text-2xl font-bold text-[#3D405B] bg-transparent outline-none border-b border-transparent focus:border-[#EAE6DF]" />
              <textarea value={lesson.summary || ""} onChange={(e) => setLesson({ ...lesson, summary: e.target.value })}
                data-testid="edit-summary" className="mt-4 w-full min-h-[140px] leading-relaxed text-[#3D405B] bg-[#F7F5F0] rounded-xl p-4 outline-none" />
              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                <div>
                  <p className="text-sm font-semibold text-[#3D405B] mb-2">Objectives</p>
                  <ul className="list-disc pl-5 text-sm text-[#6B7280] space-y-1">{(lesson.learning_objectives || []).map((o, i) => <li key={i}>{o}</li>)}</ul>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#3D405B] mb-2">Vocabulary</p>
                  <ul className="text-sm text-[#6B7280] space-y-1">{(lesson.vocabulary || []).map((v, i) => <li key={i}><b className="text-[#3D405B]">{v.term}</b> — {v.definition}</li>)}</ul>
                </div>
              </div>
              {lesson.key_facts?.length > 0 && (
                <div className="mt-6">
                  <p className="text-sm font-semibold text-[#3D405B] mb-2">Key Facts</p>
                  <ul className="list-disc pl-5 text-sm text-[#6B7280] space-y-1">{lesson.key_facts.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
              <p className="text-sm font-semibold text-[#3D405B] mt-6 mb-2">{(lesson.questions || []).length} questions · {lesson.estimated_minutes || 25} min</p>
              <div className="space-y-2">
                {(lesson.questions || []).map((q, i) => (
                  <div key={q.id || i} className="text-sm bg-[#F7F5F0] rounded-xl p-3">
                    <span className="stat text-xs text-[#81B29A]">{q.type === "multiple_choice" ? "MC" : "Written"} · {q.points}pt</span>
                    <p className="text-[#3D405B]">{i + 1}. {q.question}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button data-testid="regenerate-btn" onClick={() => setLesson(null)} className="flex items-center gap-2 rounded-full border border-[#EAE6DF] px-6 py-3 hover:border-[#E07A5F] transition-colors"><RefreshCw size={16} /> Start over</button>
              <button data-testid="save-draft-btn" onClick={() => publish(false)} disabled={publishing} className="rounded-full border border-[#EAE6DF] px-6 py-3 hover:border-[#3D405B] transition-colors">Save draft</button>
              <button data-testid="save-library-btn" onClick={() => saveToLibrary(lesson, lesson.chapter_title || `${form.subject} ${form.chapter}`, form.subject, "ai")} disabled={savingLib} className="flex items-center gap-2 rounded-full border border-[#457B9D] text-[#457B9D] px-6 py-3 font-semibold hover:bg-[#457B9D]/10 transition-colors disabled:opacity-60"><Library size={17} /> {savingLib ? "Saving…" : "Save to Library"}</button>
              <button data-testid="publish-btn" onClick={() => publish(true)} disabled={publishing}
                className="flex items-center gap-2 rounded-full bg-[#81B29A] text-white px-8 py-3 font-semibold hover:bg-[#6f9d87] transition-colors disabled:opacity-60"><CheckCircle2 size={18} /> Approve & Publish</button>
            </div>
          </div>
        )}
      </main>
      <style>{`.inp{margin-top:.25rem;width:100%;border-radius:.75rem;border:1px solid #EAE6DF;background:#fff;padding:.6rem .9rem;outline:none}.inp:focus{box-shadow:0 0 0 2px #E07A5F}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><label className="text-sm font-medium text-[#3D405B]">{label}</label>{children}</div>);
}
